<template>
  <div id="compare-similar" ref="compare-similar" :class="$style.section">
    <div :class="['layout-container', $style.container]">
      <div :class="$style.visualWrapper">
        <img
          :src="mapImage"
          :alt="t('myProperties.landing.similars.mapImageAlt')"
          :class="$style.mapImage"
          loading="lazy"
        />
        <div :class="$style.widgetsContainer">
          <img
            :src="widgetCirclesImage"
            :alt="t('myProperties.landing.similars.widgetCirclesAlt')"
            :class="$style.widgetCircles"
            loading="lazy"
          />
          <img
            :src="widgetTableImage"
            :alt="t('myProperties.landing.similars.widgetTableAlt')"
            :class="$style.widgetTable"
            loading="lazy"
          />
        </div>
      </div>

      <div :class="$style.content">
        <HgBadge :theme="badgeTheme" :class="$style.badge">
          {{ t('myProperties.landing.similars.badge') }}
        </HgBadge>
        <h2 :class="$style.title">{{ t('myProperties.landing.similars.title') }}</h2>
        <p :class="$style.description">{{ t('myProperties.landing.similars.description') }}</p>
        <HgButton
          size="large"
          :theme="isHg ? 'secondary' : 'primary'"
          :class="$style.cta"
          @click="goToOverview"
        >
          {{ t('myProperties.landing.similars.cta') }}
        </HgButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isPlatformHg } from '@/helpers/Platform/Platform';
import { getPropertyLandingImageUrl } from '@/helpers/Url/Url';
import { OVERVIEW_PAGE_PATH_SOURCE_LANDING } from '@/router/routes/constants';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { HgBadge, HgButton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, onMounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const isHg = isPlatformHg();
const { locale, t } = useI18n();
const router = useRouter();
const mapImage = isHg
  ? getPropertyLandingImageUrl('similars/similars_map_hg.png')
  : getPropertyLandingImageUrl('similars/similars_map_is24.png');
const similarsCirclesHg: Partial<Record<HgSupportedLanguage, string>> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl('similars/similars_circles_hg_de.png'),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl('similars/similars_circles_hg_en.png'),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl('similars/similars_circles_hg_fr.png'),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl('similars/similars_circles_hg_it.png'),
};

const similarsCirclesIs24: Partial<Record<HgSupportedLanguage, string>> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl('similars/similars_circles_is24_de.png'),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl('similars/similars_circles_is24_en.png'),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl('similars/similars_circles_is24_fr.png'),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl('similars/similars_circles_is24_it.png'),
};

const similarsTableHg: Partial<Record<HgSupportedLanguage, string>> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl('similars/similars_table_hg_de.png'),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl('similars/similars_table_hg_en.png'),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl('similars/similars_table_hg_fr.png'),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl('similars/similars_table_hg_it.png'),
};

const similarsTableIs24: Partial<Record<HgSupportedLanguage, string>> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl('similars/similars_table_is24_de.png'),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl('similars/similars_table_is24_en.png'),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl('similars/similars_table_is24_fr.png'),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl('similars/similars_table_is24_it.png'),
};

const widgetCirclesImage = computed(() => {
  const localeValue = locale.value as HgSupportedLanguage;
  const imagesByLocale = isHg ? similarsCirclesHg : similarsCirclesIs24;

  return imagesByLocale[localeValue] ?? imagesByLocale[HgSupportedLanguage.EN];
});

const widgetTableImage = computed(() => {
  const localeValue = locale.value as HgSupportedLanguage;
  const imagesByLocale = isHg ? similarsTableHg : similarsTableIs24;

  return imagesByLocale[localeValue] ?? imagesByLocale[HgSupportedLanguage.EN];
});

const badgeTheme = computed(() => (isHg ? 'secondary' : 'info'));

const goToOverview = () => {
  trackingClickEvent(
    TRACKING_ACTION.GO_TO_MY_REAL_ESTATE,
    TRACKING_LABEL.PROPERTY_SIMILAR_OFFERS_TEASER,
  );

  router.push(OVERVIEW_PAGE_PATH_SOURCE_LANDING[locale.value]);
};

const componentRef = useTemplateRef('compare-similar');
const route = useRoute();
onMounted(() => {
  if (route.hash === '#compare-similar') {
    componentRef.value?.scrollIntoView({ behavior: 'smooth' });
  }
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.section {
  padding: $space-100 0;
  background-color: $color-white;
}

.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.visualWrapper {
  position: relative;
  width: 100%;
  max-width: 1000px;
  display: flex;
  justify-content: center;
  padding: 0 0;

  @include grid-media-query(md) {
    padding: 0;
  }
}

.mapImage {
  width: 100%;
  height: auto;
  border-radius: $border-radius-l;
  display: block;
}

.widgetsContainer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $space-200;
  padding: 0 0;

  @include grid-media-query(md) {
    padding: 0;
  }
}

.widgetCircles,
.widgetTable {
  width: 52%;
  height: auto;
  border-radius: $border-radius;
  filter: drop-shadow(0 $space-25 $space-100 $elevation-shadow-color);
}

.widgetTable {
  width: 75%;

  @include grid-media-query(xs) {
    width: 70%;
  }

  @include grid-media-query(sm) {
    width: 52%;
  }
}

.widgetCircles {
  display: none;
  @include grid-media-query(sm) {
    display: block;
  }
}

.content {
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-100;
  padding: 0 $space-400;
}

.badge {
  display: inline-flex;
}

.title {
  @include heading-1;
  margin: 0;
  color: $color-black;

  @include grid-media-query(max-sm) {
    font-size: 24px;
  }
}

.description {
  @include body-2;
  margin: 0;
  color: $color-black;
  text-wrap: balance;
}

.cta {
  margin-top: $space-200;
  width: 100%;
  color: $color-blue-60;

  @include grid-media-query(sm) {
    width: auto;
    min-width: 300px;
  }
}
</style>
